// Конфигурация для ПКБ API
// Техническое задание: Клиент для API ГБД "Юридические лица"

export interface PKBEnvironment {
    name: string;
    baseUrl: string;
    description: string;
    isProduction: boolean;
}

export const PKB_ENVIRONMENTS: Record<string, PKBEnvironment> = {
    test: {
        name: 'Тестовая среда',
        baseUrl: 'https://fcbopenapi-gw-test.1cb.kz',
        description: 'Тестовая среда для разработки и тестирования',
        isProduction: false
    },
    production: {
        name: 'Боевая среда',
        baseUrl: 'https://fcbopenapi-gw.1cb.kz',
        description: 'Боевая среда для продакшена',
        isProduction: true
    },
    vpn_test: {
        name: 'VPN Тестовая',
        baseUrl: 'https://test2.1cb.kz',
        description: 'VPN тестовая среда',
        isProduction: false
    },
    vpn_production: {
        name: 'VPN Боевая',
        baseUrl: 'https://secure2.1cb.kz',
        description: 'VPN боевая среда',
        isProduction: true
    }
};

// Тестовые БИН для проверки
export const TEST_BINS = [
    '200540023088',
    '040140007493',
    '971240003847',
    '980440003020'
];

// Конфигурация по умолчанию
export const DEFAULT_PKB_CONFIG = {
    environment: 'test' as keyof typeof PKB_ENVIRONMENTS,
    timeout: 30000, // 30 секунд
    retryAttempts: 3,
    retryDelay: 1000, // 1 секунда
    tokenRefreshBuffer: 60000, // 1 минута до истечения токена
    enableCaching: true,
    cacheExpiry: 24 * 60 * 60 * 1000, // 24 часа
};

// Тестовые учетные данные для разработки
// ⚠️ ВНИМАНИЕ: Используйте только для тестовой среды
export const DEMO_CREDENTIALS = {
    login: 'ARGUMENTONE_Service_test',
    password: 'Ee6ae5QeD967b2G'
};

// Получение текущей среды
export const getCurrentEnvironment = (): PKBEnvironment => {
    const envKey = process.env.REACT_APP_PKB_ENV || DEFAULT_PKB_CONFIG.environment;
    return PKB_ENVIRONMENTS[envKey] || PKB_ENVIRONMENTS.test;
};

// Проверка валидности БИН
export const isValidBin = (bin: string): boolean => {
    return /^\d{12}$/.test(bin);
};

// Форматирование БИН для отображения
export const formatBin = (bin: string): string => {
    if (!isValidBin(bin)) return bin;
    return `${bin.slice(0, 4)} ${bin.slice(4, 8)} ${bin.slice(8, 12)}`;
};

// Получение URL для конкретного endpoint
export const getPKBUrl = (endpoint: string, environment?: PKBEnvironment): string => {
    const env = environment || getCurrentEnvironment();
    return `${env.baseUrl}${endpoint}`;
};

// Конфигурация заголовков
export const getPKBHeaders = (token?: string): Record<string, string> => {
    const headers: Record<string, string> = {
        'X-Client-Type': 'API',
        'Content-Type': 'application/json'
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
};

// Конфигурация для Basic авторизации
export const getBasicAuthHeaders = (login: string, password: string): Record<string, string> => {
    const authString = btoa(`${login}:${password}`);
    return {
        'Authorization': `Basic ${authString}`,
        'X-Client-Type': 'API',
        'Content-Type': 'application/json'
    };
};

// Endpoints ПКБ API
export const PKB_ENDPOINTS = {
    LOGIN: '/auth2/v1/login',
    REFRESH: '/auth2/v1/refresh',
    SEARCH_BY_BIN: '/gbdulbybin/v1/users/companies'
} as const;

// Коды ошибок ПКБ
export const PKB_ERROR_CODES = {
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    METHOD_NOT_ALLOWED: 405,
    INTERNAL_SERVER_ERROR: 500,
    SERVICE_UNAVAILABLE: 503
} as const;

// Специфичные коды ошибок ПКБ
export const PKB_AUTH_ERROR_CODES = {
    WRONG_CREDENTIALS: 'AUTH_WRONG_CREDINTIALS',
    USER_NOT_FOUND: 'AUTH_USER_NOT_FOUND',
    USER_BLOCKED: 'AUTH_USER_IS_BLOCKED',
    SYSTEM_ERROR: 'AUTH_SYSTEM_ERROR',
    NO_RIGHT: 'AUTH_USER_NO_RIGHT'
} as const;

// Сообщения об ошибках
export const PKB_ERROR_MESSAGES = {
    [PKB_ERROR_CODES.UNAUTHORIZED]: 'Ошибка авторизации. Проверьте учетные данные.',
    [PKB_ERROR_CODES.FORBIDDEN]: 'Нет прав доступа к данному ресурсу.',
    [PKB_ERROR_CODES.NOT_FOUND]: 'Компания с указанным БИН не найдена.',
    [PKB_ERROR_CODES.METHOD_NOT_ALLOWED]: 'Метод не разрешен для данного API ресурса.',
    [PKB_ERROR_CODES.INTERNAL_SERVER_ERROR]: 'Внутренняя ошибка сервера ПКБ.',
    [PKB_ERROR_CODES.SERVICE_UNAVAILABLE]: 'Сервис ПКБ временно недоступен.'
} as const;

// Сообщения для специфичных ошибок авторизации
export const PKB_AUTH_ERROR_MESSAGES = {
    [PKB_AUTH_ERROR_CODES.WRONG_CREDENTIALS]: 'Неверные учетные данные. Проверьте логин и пароль.',
    [PKB_AUTH_ERROR_CODES.USER_NOT_FOUND]: 'Пользователь не найден. Проверьте логин.',
    [PKB_AUTH_ERROR_CODES.USER_BLOCKED]: 'Пользователь заблокирован. Обратитесь к администратору.',
    [PKB_AUTH_ERROR_CODES.SYSTEM_ERROR]: 'Внутренняя ошибка системы авторизации.',
    [PKB_AUTH_ERROR_CODES.NO_RIGHT]: 'У пользователя нет прав на использование сервиса поиска по БИН. Обратитесь к администратору ПКБ для получения доступа.'
} as const;

// Получение сообщения об ошибке по коду
export const getErrorMessage = (statusCode: number): string => {
    return PKB_ERROR_MESSAGES[statusCode as keyof typeof PKB_ERROR_MESSAGES] ||
        `Неизвестная ошибка (код: ${statusCode})`;
};

// Валидация учетных данных
export const validateCredentials = (login: string, password: string): { isValid: boolean; error?: string } => {
    if (!login || !password) {
        return { isValid: false, error: 'Логин и пароль обязательны' };
    }

    if (login.length < 3) {
        return { isValid: false, error: 'Логин должен содержать минимум 3 символа' };
    }

    if (password.length < 6) {
        return { isValid: false, error: 'Пароль должен содержать минимум 6 символов' };
    }

    return { isValid: true };
};

// Настройки логирования
export const LOG_CONFIG = {
    enableConsoleLog: process.env.NODE_ENV === 'development',
    enableNetworkLog: process.env.NODE_ENV === 'development',
    logLevel: process.env.NODE_ENV === 'development' ? 'debug' : 'error'
} as const;
